/* A chapa do cartaz e a moldura do aviso vêm dos ficheiros da tranca de ordem
   porque foi lá que as duas nasceram, e porque os dois cadeados ocupam o mesmo
   canto e o mesmo topo de página. O que este desenho recusa daquele é a RÉGUA —
   ver o cabeçalho abaixo —, e não o lugar: o lugar é o que faz as duas esperas
   serem reconhecidas como espera antes de se ler uma palavra. */
import { PlacaNoCartaz } from './TrancaDeOrdem.js';
import { useT } from '../lib/idioma.js';
import { AvisoNoTopoDoCurso } from './CursoTrancado.js';
import { Relogio } from './Relogio.js';
import { diasAte, fraseDaEspera, quantoFalta, type Lancamento } from '../lib/lancamento.js';

/**
 * ── O CURSO COM DATA MARCADA (24/09/2026) ──────────────────────────────────
 *
 * **A tese.** A vitrine tem agora três cadeados, e este é o único que não pede
 * nada: *você não comprou* leva à venda, *depende de outro curso* desenha a
 * régua e nomeia o que falta, e **«ainda não é hora» não tem gesto nenhum do
 * outro lado**. O cartão afirma uma coisa só e recusa todo o resto: **quando**
 * abre. Não pede pressa, não sugere percurso e não inventa uma tarefa para a
 * espera parecer produtiva.
 *
 * **A signature: o carimbo do relógio.** No canto exato onde o cadeado de ordem
 * estampa «Trancado», este estampa «Agendado» — e troca o cadeado por um
 * relógio. O relógio é redondo de propósito: é a única silhueta curva num
 * cartaz feito de retângulos, e a três metros é a **forma**, não a palavra, que
 * separa este cadeado dos outros dois. O par lê-se junto, como dois carimbos da
 * mesma repartição: *Trancado* / *Agendado*.
 *
 * **O que fica quieto para ele funcionar.** A régua, a barra de progresso do
 * curso, o véu sobre a arte, o cadeado, o botão, o link dentro da frase (não há
 * para onde ir: o cadeado de ordem tinha saída, este é uma espera) e qualquer
 * contagem abaixo do dia. Debaixo do título fica **uma linha só** — a frase.
 *
 * ## Por que o carimbo NÃO conta os dias (e contava, até 24/09 à tarde)
 *
 * Porque a frase passou a ser derivada aqui, com a escada do core, e essa
 * escada **já é a contagem** onde a contagem é a melhor resposta: «Abre em 5
 * dias» de dois a sete, «Abre amanhã» na véspera, a hora no próprio dia. Um
 * carimbo a dizer `EM 5 DIAS` oito pixels acima de «Abre em 5 dias» é a mesma
 * medida escrita duas vezes no mesmo cartão de 176px — o acessório que se tira
 * ao espelho antes de sair de casa.
 *
 * Sobra ao carimbo o que a frase não faz: dizer, **a três metros e sem se ler**,
 * que aquele cartaz não abre hoje. Para isso uma palavra fixa basta, e é melhor:
 * o olho aprende a forma uma vez e reconhece-a em qualquer prateleira.
 */

/**
 * O relógio mudou-se para `componentes/Relogio.tsx` em 30/09/2026, quando a
 * AGENDA passou a desenhar a mesma marca no dia em que um curso estreia.
 *
 * Reexportado daqui para nenhum importador mudar — e porque é aqui que ele
 * ganhou o significado que a agenda herda: *esta coisa tem hora marcada*.
 */
export { Relogio };

/**
 * O carimbo sobre a arte: o relógio e a espécie.
 *
 * «Agendado», e não «Em breve»: *em breve* é verdade a oito dias e mentira a
 * noventa, e um carimbo que às vezes exagera ensina a não o ler. *Agendado* é o
 * que o curso é em qualquer distância — o tutor marcou uma data —, e a data
 * exata está uma linha abaixo, onde há espaço para ela.
 */
export function SeloDeLancamento() {
  const t = useT();
  return (
    <PlacaNoCartaz data-vst-lancamento="selo">
      <Relogio />
      {t('agenda.agendado')}
    </PlacaNoCartaz>
  );
}

/**
 * ── A FRASE DENTRO DA CAPA (ordem do dono, 24/09/2026) ─────────────────────
 *
 * > *«o texto de quando abre deve ficar na capa, com o véu e o cadeado, e não
 * > abaixo do título para não ocupar espaço»*
 *
 * O relógio volta a aparecer ao lado dela, e não é repetição do carimbo: ali
 * em cima ele acompanha a ESPÉCIE («Agendado»), aqui ele encabeça a DATA. Dois
 * pares de olhos diferentes leem os dois — o que passa a três metros vê o
 * carimbo; o que já parou no cartão lê a linha.
 *
 * `--texto` e não `--texto-suave`: sobre o véu, a tinta sobe de peso. A
 * medição do contraste está em `VeuDoCartaz`.
 */
export function EstadoNoCartaz({ lancamento }: { lancamento: Lancamento }) {
  const t = useT();
  return (
    /* A fila mudou-se do slot para aqui em 14/10/2026, quando o motivo da
       tranca passou a ser o segundo inquilino de `[data-vst-estado-do-cartaz]`
       — ver `VeuDoCartaz`. Relógio e frase lado a lado é o desenho DESTA
       espécie; o slot, que agora carrega as duas, não pode impô-lo. */
    <span className="flex items-center gap-1.5">
      <Relogio />
      {/* A MESMA marca da linha que vivia debaixo do título — `frase`.
          Mudou de lugar, não de identidade: quem mede «a frase sai da escada do
          core, e não da reserva do servidor» continua a medir a mesma coisa, e
          quem quer provar que ela está DENTRO da capa procura o
          `[data-vst-estado-do-cartaz]` que a contém. */}
      <span data-vst-lancamento="frase" className="truncate">
        {fraseDaEspera(lancamento, undefined, t)}
      </span>
    </span>
  );
}

/**
 * A linha debaixo do título do cartão: a frase, e mais nada.
 *
 * Sem a contagem ao lado, e aqui a razão é mais forte do que na página: no
 * cartão a frase já é curta por desenho, e «Abre em 12 de março · faltam 43
 * dias» não cabe em 176px sem virar três linhas de legenda sobre um cartaz.
 * Quem quiser a distância abre o curso. O registo é o da legenda que já vive
 * neste lugar («8 aulas»), que é o `compacto` da tranca de ordem: 12px,
 * `--texto-suave`.
 */
export function FraseDoLancamento({
  lancamento,
  /**
   * `compacto` é o padrão porque o cartaz é o caso comum — e é o inverso do
   * `MotivoDaTranca`, onde o padrão é o tamanho de leitura. A razão da
   * inversão: aquele nasceu na página do curso, onde há coluna; este nasceu no
   * cartão de 176px, e só a etapa da trilha lhe dá largura de frase.
   */
  compacto = true,
}: {
  lancamento: Lancamento;
  compacto?: boolean;
}) {
  const t = useT();
  return (
    <p
      className={compacto ? 'text-xs leading-snug' : 'text-sm leading-snug'}
      style={{ color: 'var(--texto-suave)' }}
      data-vst-lancamento="frase"
    >
      {fraseDaEspera(lancamento, undefined, t)}
    </p>
  );
}

/**
 * ── A PÁGINA DO CURSO QUE AINDA NÃO ESTREOU ────────────────────────────────
 *
 * A mesma decisão da página do curso trancado por ordem, e pelo mesmo motivo: o
 * link direto, o favorito e o e-mail de ontem entram por `/c/:id` sem passar por
 * prateleira nenhuma. **A página deixa de ser porta e passa a ser índice** — o
 * redirecionamento para a aula para (ele levaria ao 403), o aviso vem antes da
 * descrição e a lista de aulas fica inteira, sem uma linha clicável. A lista é a
 * única coisa na tela que diz **o que vai abrir**, e escondê-la trocaria um
 * clique recusado por uma página que não explica o que está a guardar.
 *
 * ## O que muda em relação ao aviso da tranca de ordem
 *
 * Lá a linha grande diz «Este curso ainda não abriu.» e o motivo vem por baixo,
 * porque o motivo fala de **outro** curso e precisa da moldura. Aqui a frase do
 * core já é as duas coisas na mesma oração — «Abre em 12 de março» diz que não
 * abriu **e** quando abre —, então a moldura sai: ela seria o acessório que se
 * tira ao espelho antes de sair de casa.
 *
 * A distância entra ao lado, em `--texto-suave` e meio tamanho abaixo, e **só no
 * degrau da data** — de oito dias para cima. É lá que ela acrescenta: ninguém
 * subtrai «12 de março» de hoje a olho, e abaixo disso a própria frase já conta
 * os dias. Ver `quantoFalta`, que explica a escada inteira.
 */
export function AvisoDoLancamento({ lancamento }: { lancamento: Lancamento }) {
  /* O MESMO instante para a frase e para a contagem. Dois `new Date()` seriam
     dois relógios a ler-se sozinhos, e na virada da meia-noite um diria «Abre
     amanhã» e o outro «faltam 0 dias» no mesmo parágrafo. */
  const t = useT();
  const agora = new Date();
  const falta = quantoFalta(diasAte(lancamento.em, agora));
  return (
    <AvisoNoTopoDoCurso
      data-vst-lancamento="curso"
      glifo={<Relogio tamanho={14} />}
      titulo={
        <span>
          {fraseDaEspera(lancamento, agora, t)}
          {falta && (
            /* Na mesma linha da data, e não numa abaixo: são a mesma resposta
               vista de dois lados — o dia e a distância até ele. Duas linhas
               fariam duas perguntas parecerem duas. */
            <span className="ml-2 text-sm font-normal" style={{ color: 'var(--texto-suave)' }}>
              · {falta}
            </span>
          )}
        </span>
      }
    />
  );
}
